import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

/**
 * Verified Boutique Badge Component
 * Fix: Allow text wrap (fully visible), never ellipsis
 */
export function VerifiedBoutiqueBadge() {
  const { t } = useTranslation();
  
  return (
    <span
      className="verified-boutique-badge"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '5px 10px',
        borderRadius: 12,
        fontSize: 11,
        fontWeight: 700,
        background: 'rgba(255,165,0,0.14)',
        border: '1px solid rgba(255,165,0,0.25)',
        whiteSpace: 'normal',       /* Allow wrap for full visibility */
        wordBreak: 'break-word',    /* Prevent overflow */
        lineHeight: 1.3,
        maxWidth: '100%',
      }}
      data-testid="verified-boutique-badge"
    >
      ✅ {t('ladiesHub.verifiedBoutique')}
    </span>
  );
}

/**
 * Most Popular in Ladies Section Component
 */
export function MostPopularLadiesSection({ onOpenAd }) {
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [popular, setPopular] = useState([]);
  const isArabic = i18n.language?.startsWith('ar');

  useEffect(() => {
    let mounted = true;
    
    (async () => {
      try {
        setLoading(true);
        const response = await fetch(`${BACKEND_URL}/api/ladies/popular?limit=10`);
        
        if (!response.ok) {
          throw new Error('Failed to fetch popular ads');
        }
        
        const data = await response.json();
        
        if (!mounted) return;
        setPopular(data.popular || []);
      } catch (error) {
        console.error('Error fetching popular ads:', error);
        if (mounted) setPopular([]);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    
    return () => {
      mounted = false;
    };
  }, []);

  if (loading) return null;
  if (!popular.length) return null;

  return (
    <section 
      style={{ padding: '12px 16px', marginBottom: 16 }}
      data-testid="most-popular-ladies-section"
    >
      <div style={{ 
        fontWeight: 700, 
        fontSize: 16, 
        marginBottom: 10,
        color: '#333'
      }}>
        {t('ladiesHub.mostPopular')}
      </div>

      <div
        style={{
          display: 'flex',
          gap: 12,
          overflowX: 'auto',
          paddingBottom: 6,
          scrollbarWidth: 'thin',
        }}
      >
        {popular.map((ad) => (
          <button
            key={ad.ad_id}
            onClick={() => onOpenAd(ad.ad_id)}
            style={{
              minWidth: 180,
              maxWidth: 180,
              borderRadius: 16,
              border: '1px solid rgba(0,0,0,0.08)',
              background: '#fff',
              textAlign: isArabic ? 'right' : 'left',
              padding: 10,
              cursor: 'pointer',
              transition: 'all 0.2s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
            }}
            data-testid={`popular-ad-${ad.ad_id}`}
          >
            <div
              style={{
                height: 110,
                borderRadius: 14,
                overflow: 'hidden',
                background: 'rgba(0,0,0,0.04)',
                marginBottom: 8,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {ad.images?.[0] ? (
                <img
                  src={
                    ad.images[0].startsWith('http')
                      ? ad.images[0]
                      : `${BACKEND_URL}${ad.images[0]}`
                  }
                  alt={ad.title}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                  }}
                  loading="lazy"
                />
              ) : (
                <span style={{ opacity: 0.6, fontSize: 12 }}>📦</span>
              )}
            </div>

            <div
              style={{
                fontWeight: 700,
                fontSize: 14,
                lineHeight: 1.3,
                marginBottom: 6,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                color: '#222',
              }}
            >
              {ad.title}
            </div>

            <div style={{ fontWeight: 800, fontSize: 15, color: '#FF6B6B' }}>
              {ad.currency || 'SDG'} {ad.price?.toLocaleString?.() ?? ad.price}
            </div>
          </button>
        ))}
      </div>
    </section>
  );
}

/**
 * Helper function to check if a seller is verified
 */
export function isVerifiedBoutique(seller) {
  return seller?.verification?.status === 'verified_boutique';
}

/**
 * Ad Card Header Tags - Shows hub tag and verification badge
 */
export function AdCardHeaderTags({ ad, seller }) {
  const { t, i18n } = useTranslation();
  const isArabic = i18n.language?.startsWith('ar');
  const isLadiesAd = ad?.hub === 'ladies';
  const isVerified = isVerifiedBoutique(seller);

  if (!isLadiesAd && !isVerified) return null;

  return (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
      {isLadiesAd && (
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            padding: '4px 10px',
            borderRadius: 999,
            fontSize: 12,
            fontWeight: 800,
            background: 'rgba(255,120,120,0.14)',
            border: '1px solid rgba(255,120,120,0.25)',
          }}
          data-testid="ladies-hub-tag"
        >
          🧡 {isArabic ? 'قسم السيدات' : 'Ladies Hub'}
        </span>
      )}

      {isVerified && <VerifiedBoutiqueBadge />}
    </div>
  );
}

/**
 * Helper to track ad events (views, favorites, contact clicks)
 */
export async function trackAdEvent(adId, event) {
  if (!adId || !event) return;
  
  try {
    await fetch(`${BACKEND_URL}/api/ads/${adId}/track`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ event }),
    });
  } catch (error) {
    console.error(`Failed to track ${event}:`, error);
  }
}
